
import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch } from "react-icons/fa";
import InfiniteScroll from "react-infinite-scroll-component";

import Loader from "../../components/Loader";
import OrgCard from "../../components/OrgCard";
import { api } from "../../app/Api/ApiSlice";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;

const MyOrgsList = () => {
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const [userOrgs, setUserOrgs] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const [searchedTerm, setSearchedTerm] = useState("");
    const [activeSearch, setActiveSearch] = useState("");

    const [errorMessage, setErrorMessage] = useState("");

    // Refs provide synchronous protection against duplicate requests.
    const requestIdRef = useRef(0);
    const loadingMoreRef = useRef(false);
    const mountedRef = useRef(false);

    // Prevent state updates after the component unmounts.
    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            requestIdRef.current += 1;
        };
    }, []);

    /**
     * Fetch organizations from the backend.
     *
     * append = false: replace the existing list.
     * append = true: append the next page.
     */
    const getUserOrgs = useCallback(
        async (pageNumber = 1, search = "", append = false) => {
            if (
                !Number.isSafeInteger(pageNumber) ||
                pageNumber < 1
            ) {
                return false;
            }

            // Prevent duplicate pagination requests synchronously.
            if (append && loadingMoreRef.current) {
                return false;
            }

            if (append) {
                loadingMoreRef.current = true;
                setIsLoadingMore(true);
            } else {
                // Invalidate any older request.
                requestIdRef.current += 1;
                setIsLoading(true);
                setErrorMessage("");
            }

            const requestId = requestIdRef.current;

            try {
                const response = await api.get("/organizations", {
                    params: {
                        page: pageNumber,
                        limit: PAGE_SIZE,
                        search: search.trim(),
                    },
                    timeout: 15000,
                });

                // Ignore outdated responses.
                if (
                    !mountedRef.current ||
                    requestId !== requestIdRef.current
                ) {
                    return false;
                }

                const organizations = response?.data?.organizations;
                const moreAvailable = response?.data?.hasmore;

                // Validate the backend response.
                if (!Array.isArray(organizations)) {
                    throw new Error(
                        "Invalid response: organizations must be an array."
                    );
                }

                if (typeof moreAvailable !== "boolean") {
                    throw new Error(
                        "Invalid response: hasmore must be a boolean."
                    );
                }

                if (append) {
                    setUserOrgs((previous) => {
                        const existingIds = new Set(
                            previous
                                .filter((org) => org?._id != null)
                                .map((org) => String(org._id))
                        );

                        const uniqueOrganizations = organizations.filter(
                            (org) => {
                                if (org?._id == null) {
                                    return false;
                                }

                                const id = String(org._id);

                                if (existingIds.has(id)) {
                                    return false;
                                }

                                existingIds.add(id);
                                return true;
                            }
                        );

                        return [
                            ...previous,
                            ...uniqueOrganizations,
                        ];
                    });
                } else {
                    setUserOrgs(organizations);
                }

                // Advance pagination only after a successful response.
                setPage(pageNumber);

                setHasMore(
                    moreAvailable && organizations.length > 0
                );

                setErrorMessage("");

                return true;
            } catch (error) {
                if (
                    mountedRef.current &&
                    requestId === requestIdRef.current
                ) {
                    const message =
                        error?.response?.data?.message ||
                        error?.message ||
                        "Error loading organizations.";

                    setErrorMessage(message);

                    console.error(
                        "Fetch organizations error:",
                        error?.response?.status,
                        error?.response?.data || error?.message
                    );

                    toast.error(
                        "Error loading organizations. Please try again."
                    );
                }

                // Preserve existing organizations and page on failure.
                return false;
            } finally {
                if (mountedRef.current) {
                    if (!append) {
                        setIsLoading(false);
                    }

                    if (
                        append &&
                        requestId === requestIdRef.current
                    ) {
                        setIsLoadingMore(false);
                    }
                }

                if (append) {
                    loadingMoreRef.current = false;
                }
            }
        },
        []
    );

    /**
     * Debounced search.
     * Each new search invalidates previous responses immediately.
     */
    useEffect(() => {
        const search = searchedTerm.trim();

        // Invalidate pending requests as soon as this effect runs.
        requestIdRef.current += 1;

        // Reset pagination and the previous results.
        setActiveSearch(search);
        setPage(1);
        setHasMore(true);
        setUserOrgs([]);
        setErrorMessage("");
        setIsLoading(true);

        const timer = setTimeout(() => {
            getUserOrgs(1, search, false);
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
        };
    }, [searchedTerm, getUserOrgs]);

    /**
     * Load the next page.
     */
    const fetchMore = useCallback(() => {
        if (
            !hasMore ||
            isLoading ||
            isLoadingMore ||
            loadingMoreRef.current
        ) {
            return;
        }

        getUserOrgs(page + 1, activeSearch, true);
    }, [
        hasMore,
        isLoading,
        isLoadingMore,
        page,
        activeSearch,
        getUserOrgs,
    ]);

    /**
     * Retry loading the current page after a failure.
     */
    const handleRetry = useCallback(() => {
        if (loadingMoreRef.current) {
            return;
        }

        if (userOrgs.length === 0) {
            getUserOrgs(1, activeSearch, false);
        } else {
            getUserOrgs(page + 1, activeSearch, true);
        }
    }, [getUserOrgs, userOrgs.length, activeSearch, page]);

    return (
        <div className="orgpage-body">
            {/* HEADER */}
            <div className="orglist-page-header">
                <h2 className="page-title">
                    Organizations
                </h2>

                <button
                    type="button"
                    className="centered-content"
                    onClick={() => navigate("/organizations/new")}
                    aria-label="Create organization"
                >
                    <FaPlus />
                </button>
            </div>

            {/* SEARCH */}
            <form
                className="search-orgs"
                onSubmit={(event) => event.preventDefault()}
            >
                <input
                    type="search"
                    className="org-search"
                    placeholder="Search organizations..."
                    value={searchedTerm}
                    onChange={(event) =>
                        setSearchedTerm(event.target.value)
                    }
                    autoComplete="off"
                    aria-label="Search organizations"
                />

                <button
                    type="submit"
                    aria-label="Search organizations"
                >
                    <FaSearch />
                </button>
            </form>

            {/* ORGANIZATION LIST */}
            <div
                className="org-list-container"
                id="scrollableDiv"
            >
                {isLoading && userOrgs.length === 0 ? (
                    <Loader />
                ) : userOrgs.length === 0 ? (
                    <div className="org-empty-state">
                        {errorMessage ? (
                            <>
                                <p>
                                    Unable to load organizations.
                                </p>

                                <button
                                    type="button"
                                    onClick={handleRetry}
                                >
                                    Retry
                                </button>
                            </>
                        ) : (
                            <p className="not-found-msg">
                                No organizations found
                            </p>
                        )}
                    </div>
                ) : (
                    <>
                        {errorMessage && (
                            <div className="org-load-error">
                                <p>
                                    Unable to load more organizations.
                                </p>

                                <button
                                    type="button"
                                    onClick={handleRetry}
                                    className="form-btn"
                                >
                                    Retry
                                </button>
                            </div>
                        )}

                        <InfiniteScroll
                            dataLength={userOrgs.length}
                            next={fetchMore}
                            hasMore={hasMore}
                            loader={
                                isLoadingMore ? <Loader /> : null
                            }
                            scrollableTarget="scrollableDiv"
                            scrollThreshold={0.5}
                        >
                            {userOrgs.map((org, index) => (
                                <OrgCard
                                    org={org}
                                    key={
                                        org?._id != null
                                            ? String(org._id)
                                            : `organization-${index}`
                                    }
                                />
                            ))}
                        </InfiniteScroll>

                        {!hasMore && userOrgs.length > 0 && (
                            <p className="org-end-message">
                                No more organizations
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default MyOrgsList;
